import { useEffect, useRef } from "react"

interface UseScrollSentinelOptions {
  // Fired each time the sentinel enters the viewport. The hook is
  // deliberately light: debouncing and "don't fetch if already fetching"
  // belong in the consumer, which knows the loading state.
  onEnter: () => void
  // Enable/disable the observer without unmounting the sentinel. Used to
  // stop observing once the list has been fully loaded.
  enabled?: boolean
  // Distance from the viewport edge at which the sentinel fires, as a
  // CSS-style margin. Defaults to "400px 0px" so we prefetch a screen
  // ahead and the user never sees a loading gap.
  rootMargin?: string
}

// Returns a ref to attach to an invisible sentinel element at the bottom
// of the list. IntersectionObserver is the modern replacement for scroll-
// listener + getBoundingClientRect — no layout thrash, runs off the main
// thread, and never fires for offscreen tabs.
export const useScrollSentinel = <T extends HTMLElement = HTMLDivElement>({
  onEnter,
  enabled = true,
  rootMargin = "400px 0px",
}: UseScrollSentinelOptions) => {
  const sentinelRef = useRef<T | null>(null)
  // Keep the latest callback in a ref so we don't reinstantiate the
  // observer every time the consumer re-renders — the observer lives for
  // the component's lifetime, but the callback it invokes stays fresh.
  const onEnterRef = useRef(onEnter)
  onEnterRef.current = onEnter

  useEffect(() => {
    if (!enabled) return
    const node = sentinelRef.current
    if (!node) return
    if (typeof IntersectionObserver === "undefined") return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onEnterRef.current()
        })
      },
      { rootMargin },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [enabled, rootMargin])

  return sentinelRef
}
